// REACT
import React from "react";
import { useParams, useHistory } from "react-router-dom";

// MATERIAL UI
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import { Close } from "@mui/icons-material";

// LAYOUT
import Layout from "src/components/core/Layout";

// COMPONENTS
import TransferDetailContent from "src/components/transfers/TransferDetailContent";

interface TransferDetailRouteParams {
  id: string;
}

/**
 * `/transfers/:id` route. Renders the transfer detail inside an MUI Dialog at
 * every breakpoint — fullscreen on mobile, centered md-width on desktop — so
 * the inbound `ACTION_REQUIRED` form feels equivalent to the modal-driven
 * outbound withdrawal flow rather than a standalone page. The URL stays
 * deep-linkable from email + the transactions-list pill.
 */
const TransferDetail: React.FC = () => {
  const { id } = useParams<TransferDetailRouteParams>();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme as any).breakpoints.down("md"));
  const [useDefaultTheme, toggleTheme] = React.useReducer(
    (t: boolean): boolean => !t,
    true,
  );

  // Pill clicks / dev simulator land here via `history.push`, so `goBack` puts
  // the user back where they were. Deep-links from email give us no history
  // entry behind us — fall back to /portfolio so the dialog never traps.
  const handleClose = () => {
    if ((history as any).length > 1) {
      history.goBack();
    } else {
      history.replace("/portfolio");
    }
  };

  return (
    <Layout toggleTheme={toggleTheme} useDefaultTheme={useDefaultTheme}>
      <Dialog
        fullScreen={isMobile}
        fullWidth
        maxWidth="md"
        open
        onClose={handleClose}
        data-testid="transfer-detail-dialog">
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
          Travel Rule - Compliance
          <IconButton onClick={handleClose} data-testid="transfer-detail-close">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TransferDetailContent txId={id} />
          </Box>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default TransferDetail;
